<template>
    <div class="pricing-cards">
        <div v-for="(card, index) in cardOptionsFiltered" :key="index" class="pricing-cards--item">
            <span v-if="isSubscriptionActive(card)" class="pricing-cards--item__current">
                Current Plan
                <span v-if="$auth.getPractice().subscription === PracticePlanEnum.TRIAL_PERIOD">(TRIAL)</span>
            </span>
            <common-content-spacer height="small">
                <common-content-spacer height="small" class="pricing-cards__info">
                    <p class="pricing-cards--item__title">{{ card.title }}</p>
                    <p class="pricing-cards--item__availability">{{ card.availability }}</p>
                    <div v-for="(item, i) in card.for_list" :key="i" class="pricing-cards--item__list">
                        <p>{{ item }}</p>
                    </div>
                </common-content-spacer>
                <common-content-spacer height="tiny" class="pricing-cards--item__pricing">
                    <p class="pricing-cards--item__pricing--value">{{ getPrice(card.subscription) }}</p>
                    <p class="pricing-cards--item__pricing--text">{{ getPriceText(card.subscription) }}</p>
                    <p class="pricing-cards--item__pricing--text">{{ getPriceValue(card.subscription) }}</p>
                </common-content-spacer>
                <div
                    v-if="(!isMobileLarge || card.subscription === PracticePlanEnum.PREMIUM) && showButtons"
                    class="pricing-cards--item__start"
                >
                    <common-button
                        v-if="showUpgradeButton(card.subscription)"
                        :label="`UPGRADE TO ${card.subscription}`"
                        :isDisabled="$auth.isPracticePremium"
                        :isFluid="false"
                        size="large"
                        type="primary"
                        @click="emitEvent({ name: 'upgrade' })"
                    ></common-button>
                </div>
                <div class="pricing-cards--item__features">
                    <p class="pricing-cards--item__features--button" @click="card.expanded = !card.expanded">
                        <common-page-header size="small" heading="View Features"></common-page-header>
                        <common-icon :name="card.expanded ? 'minus' : 'menu-arrow'" size="large"></common-icon>
                    </p>

                    <transition name="slide-up">
                        <div v-show="card.expanded">
                            <billing-features-list
                                :featuresList="card.feature_list"
                                :isHeaderCenter="false"
                            ></billing-features-list>
                            <p class="pricing-cards--item__features--fee">{{ card.fee }}</p>
                        </div>
                    </transition>
                </div>
            </common-content-spacer>
        </div>
    </div>
</template>

<script>
    import cardOptions from 'components/page-elements/practice/billing/cardOptions.json'
    import cloneDeep from 'lodash.clonedeep'
    import PracticePlanEnum from '@/enums/PracticePlanEnum'
    import BillingFeaturesList from 'components/page-elements/practice/billing/BillingFeaturesList'
    import ScreenSizeDetectionMixin from 'mixins/ScreenSizeDetectionMixin'
    import PracticeTypeEnum from '@/enums/PracticeTypeEnum'

    export default {
        mixins: [ScreenSizeDetectionMixin],

        components: { BillingFeaturesList },

        props: {
            showButtons: { type: Boolean, required: false, default: true },
        },

        data() {
            return {
                cardOptions: [],
                PracticePlanEnum,
                PracticeTypeEnum,
            }
        },

        computed: {
            cardOptionsFiltered() {
                if (this.$auth.getPractice().practiceType === PracticeTypeEnum.ANCILLARY) {
                    return this.cardOptions.filter((item) => item.subscription !== PracticePlanEnum.PREMIUM)
                }
                if (this.$auth.getPractice().practiceType === PracticeTypeEnum.PHYSICIAN) {
                    return this.cardOptions.filter((item) => item.subscription !== PracticePlanEnum.ENTERPRISE)
                }
                return this.cardOptions
            },
        },

        methods: {
            isSubscriptionActive(card) {
                if (
                    this.$auth.getPractice().subscription === PracticePlanEnum.NETWORK &&
                    card.subscription === PracticePlanEnum.NETWORK
                ) {
                    return true
                }
                if (
                    this.$auth.getPractice().subscription === PracticePlanEnum.TRIAL_PERIOD &&
                    this.$auth.getPractice().practiceType === PracticeTypeEnum.PHYSICIAN &&
                    card.subscription === PracticePlanEnum.PREMIUM
                ) {
                    return true
                }
                if (
                    this.$auth.getPractice().subscription === PracticePlanEnum.TRIAL_PERIOD &&
                    this.$auth.getPractice().practiceType === PracticeTypeEnum.ANCILLARY &&
                    card.subscription === PracticePlanEnum.ENTERPRISE
                ) {
                    return true
                }
                return false
            },

            showUpgradeButton(subscription) {
                return (
                    (this.$auth.getPractice().practiceType === PracticeTypeEnum.ANCILLARY &&
                        subscription === PracticePlanEnum.ENTERPRISE) ||
                    (this.$auth.getPractice().practiceType === PracticeTypeEnum.PHYSICIAN && subscription === 'PREMIUM')
                )
            },

            getPrice(subscription) {
                if (subscription === PracticePlanEnum.NETWORK) {
                    return '$0'
                }

                if (subscription === PracticePlanEnum.PREMIUM) {
                    return '$69*'
                }

                if (subscription === PracticePlanEnum.ENTERPRISE) {
                    return '$169*'
                }

                return ''
            },

            getPriceText(subscription) {
                if (subscription === PracticePlanEnum.NETWORK) {
                    return '(Free Forever)'
                }

                if (subscription === PracticePlanEnum.PREMIUM) {
                    return 'Per user/month billed annually'
                }

                if (subscription === PracticePlanEnum.ENTERPRISE) {
                    return 'Per location/month billed annually'
                }

                return ''
            },

            getPriceValue(subscription) {
                if (subscription === PracticePlanEnum.NETWORK) {
                    return 'Default plan after PREMIUM trial period'
                }

                if (subscription === PracticePlanEnum.PREMIUM) {
                    return '($99 billed monthly)'
                }

                if (subscription === PracticePlanEnum.ENTERPRISE) {
                    return '($199 billed monthly)'
                }

                return ''
            },
        },

        mounted() {
            this.cardOptions = cloneDeep(cardOptions.cards)
        },
    }
</script>

<style scoped lang="scss">
    @import 'styles/setup/_variables.scss';
    @import 'styles/setup/_mixins.scss';

    .pricing-cards {
        @include breakpoint(smartphone-large) {
            flex-wrap: wrap;
            height: 100vh;
        }
        display: flex;
        justify-content: space-between;
        overflow-y: auto;
        padding: 20px;
        height: 80vh;

        &__info {
            @include breakpoint(tablet) {
                height: 320px;
            }
            @include breakpoint(smartphone-large) {
                height: auto;
            }
            height: 260px;
        }

        &--item {
            @include breakpoint(smartphone-large) {
                width: 100%;
                margin-bottom: 40px;
            }
            width: calc(100% / 2);
            background-color: #f6fcff;
            margin: 0 10px;
            padding: 20px 5px;
            position: relative;

            &__current {
                background-color: $color--accent-9;
                border-radius: 15px;
                padding: 3px 15px;
                position: absolute;
                color: $color--txt-header-3;
                top: 0;
                transform: translateY(-50%);
                font-size: $font-size--hint;
                font-weight: 500;
                z-index: $layer--notification;
                left: 10px;
            }

            &__title {
                color: #001d50;
                font-family: 'Poppins', Sans-serif;
                font-size: 28px;
                font-weight: 700;
                letter-spacing: 1px;
                text-align: center;

                &::after {
                    content: '.';
                    color: #13b3e3;
                    font-size: 45px;
                    letter-spacing: 1.71px;
                    line-height: 0.7;
                }
            }

            &__availability {
                text-align: center;
                color: #001d50;
                font-weight: 500;
            }

            &__list {
                p {
                    color: #000000;
                    font-family: 'Poppins', Sans-serif;
                    font-size: 14px;
                    font-weight: 400;
                    padding: 0 20px;
                    position: relative;

                    &:before {
                        content: '';
                        background: #13b3e3;
                        display: block;
                        width: 4px;
                        height: 4px;
                        position: absolute;
                        left: 0;
                        top: 9px;
                        border-radius: 100%;
                    }
                }
            }

            &__pricing {
                @include breakpoint(tablet) {
                    height: 140px;
                }
                @include breakpoint(smartphone-large) {
                    height: auto;
                }
                height: 150px;
                &--value {
                    text-align: center;
                    color: #001d50;
                    font-family: 'Poppins', Sans-serif;
                    font-size: 32px;
                    font-weight: 600;
                    letter-spacing: 1px;
                }

                &--text,
                &--free {
                    text-align: center;
                    text-align: center;
                    color: #001d50;
                    font-family: 'Poppins', Sans-serif;
                    font-size: 14px;
                    font-weight: 400;
                }
            }

            &__start {
                text-align: center;
                height: 70px;
                padding: 0 20px;
            }

            &__features {
                background-color: #f6fcff;
                padding: 10px;

                &--button {
                    cursor: pointer;
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    color: $color--accent-1;

                    &:hover {
                        opacity: 0.8;
                    }
                }

                &--fee {
                    color: #7a7a7a;
                    font-family: 'Poppins', Sans-serif;
                    font-size: 12px;
                    font-weight: 400;
                    letter-spacing: -0.1px;
                }
            }
        }
    }
</style>
